import { injectable } from 'inversify';
import 'reflect-metadata';
import { AxiosError } from 'axios';
import axiosClient from './axios-client';
import { ERRORS } from '../constants';
import { ApiResponse } from '../utils/api-response.utils';
import type {
  CreateTicketImageReadUrlsResponse,
  CreateTicketRequest,
  CreateTicketResponse,
  CreateTicketUploadUrlsRequest,
  CreateTicketUploadUrlsResponse,
  GetMyTicketResponse,
  ListMyTicketsResponse,
} from '../models/tickets.model';

export type ListMyTicketsParams = {
  page: number;
  pageSize: number;
  // comma-separated list of TICKET_STATUS values
  status?: string;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
};

@injectable()
export class TicketService {
  async createUploadUrls(
    request: CreateTicketUploadUrlsRequest
  ): Promise<CreateTicketUploadUrlsResponse> {
    return this.unwrap(
      axiosClient.post<ApiResponse<CreateTicketUploadUrlsResponse>>(
        '/api/tickets/upload-urls',
        request
      )
    );
  }

  async createTicket(
    request: CreateTicketRequest
  ): Promise<CreateTicketResponse> {
    return this.unwrap(
      axiosClient.post<ApiResponse<CreateTicketResponse>>(
        '/api/tickets',
        request
      )
    );
  }

  async listMyTickets(
    params: ListMyTicketsParams
  ): Promise<ListMyTicketsResponse> {
    return this.unwrap(
      axiosClient.get<ApiResponse<ListMyTicketsResponse>>('/api/tickets', {
        params,
      })
    );
  }

  async getMyTicket(ticketId: string): Promise<GetMyTicketResponse> {
    return this.unwrap(
      axiosClient.get<ApiResponse<GetMyTicketResponse>>(
        `/api/tickets/${ticketId}`
      )
    );
  }

  async createImageReadUrls(
    ticketId: string,
    attachmentIds: string[]
  ): Promise<CreateTicketImageReadUrlsResponse> {
    return this.unwrap(
      axiosClient.post<ApiResponse<CreateTicketImageReadUrlsResponse>>(
        `/api/tickets/${ticketId}/images/read-urls`,
        { attachmentIds }
      )
    );
  }

  // Direct browser POST to the presigned S3 endpoint. Plain fetch on purpose:
  // axiosClient would attach an Authorization header, which breaks the
  // presigned policy. The signed fields go first and the file last — S3
  // ignores anything that follows the `file` part.
  async uploadAttachment(
    uploadUrl: string,
    uploadFields: Record<string, string>,
    file: File
  ): Promise<void> {
    const form = new FormData();
    Object.entries(uploadFields).forEach(([key, value]) =>
      form.append(key, value)
    );
    form.append('file', file);

    const response = await fetch(uploadUrl, {
      method: 'POST',
      body: form,
    });

    if (!response.ok) {
      throw new Error(ERRORS.unexpectedError);
    }
  }

  private async unwrap<T>(
    request: Promise<{ data: ApiResponse<T> }>
  ): Promise<T> {
    try {
      const response = await request;
      return response.data.data as T;
    } catch (error) {
      const serverMessage = (error as AxiosError<ApiResponse<T>>)?.response
        ?.data?.error;
      throw new Error(serverMessage || ERRORS.unexpectedError);
    }
  }
}
